import {
  IdcardOutlined,
  DownloadOutlined,
  ColumnHeightOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Row,
  Col,
  Input,
  Table,
  Select,
  message,
  Spin,
  Checkbox,
  Dropdown,
  Tooltip,
} from "antd";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Column definitions for the Custom Columns selector ───────────────────────
const ALL_COLUMNS = [
  {
    key: "empid",
    title: "Employee Code",
    defaultVisible: true,
    column: { title: "Employee Code", dataIndex: "emp_ID", key: "empid" },
  },
  {
    key: "employeename",
    title: "Employee Name",
    defaultVisible: true,
    column: { title: "Employee Name", dataIndex: "Emp_name", key: "employeename" },
  },
  {
    key: "Date",
    title: "Date",
    defaultVisible: true,
    column: { title: "Date", dataIndex: "date", key: "Date" },
  },
  {
    key: "time",
    title: "Punch-In Time",
    defaultVisible: true,
    column: { title: "Punch-In Time", dataIndex: "Time", key: "time" },
  },
  {
    key: "organisation",
    title: "Company",
    defaultVisible: true,
    column: {
      title: "Company",
      key: "organisation",
      render: (_, record) => record.employee_details?.organisation || "-",
    },
  },
  {
    key: "department",
    title: "Department",
    defaultVisible: true,
    column: {
      title: "Department",
      key: "department",
      render: (_, record) => record.employee_details?.department || "-",
    },
  },
  {
    key: "designation",
    title: "Designation",
    defaultVisible: true,
    column: {
      title: "Designation",
      key: "designation",
      render: (_, record) => record.employee_details?.designation || "-",
    },
  },
];

export default function FingerPrintSite() {
  const { authData, backUrl } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const isMobile = window.innerWidth < 768;

  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [filters, setFilters] = useState({
    emp_code: "",
    emp_name: "",
    organisation: "",
    department: "",
    designation: "",
    from_date: null,
    to_date: null,
  });

  const [searchTimer, setSearchTimer] = useState(null);

  // ── Column visibility ─────────────────────────────────────────────────────
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("fingerPrintSite_visibleColumns");
    if (saved) { try { return JSON.parse(saved); } catch (_) { } }
    return ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
  });
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("fingerPrintSite_visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const handleColumnToggle = (key) => {
    setVisibleColumns((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };
  const handleSelectAll = () => {
    setVisibleColumns(ALL_COLUMNS.map((c) => c.key));
  };

  const handleDeselectAll = () => {
    setVisibleColumns([]);
  };

  const handleResetDefault = () => {
    setVisibleColumns(
      ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );
  };

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch organisations:", err);
      message.error("Failed to fetch organisations");
    }
  };

  const fetchDepartments = async (orgId) => {
    if (!orgId) {
      setDepartments([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${orgId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDepartments(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch departments:", err);
      message.error("Failed to fetch departments");
      setDepartments([]);
    }
  };

  const fetchDesignations = async (deptId) => {
    if (!deptId) {
      setDesignations([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${deptId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDesignations(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch designations:", err);
      message.error("Failed to fetch designations");
      setDesignations([]);
    }
  };

  const fetchData = async (resetPage = false) => {
    setLoading(true);
    try {
      const params = {
        page: resetPage ? 1 : currentPage,
        page_size: pageSize,
      };

      if (filters.emp_code) params.empid = filters.emp_code;
      if (filters.emp_name) params.employeename = filters.emp_name;
      if (filters.organisation) params.organisation = filters.organisation;
      if (filters.department) params.department = filters.department;
      if (filters.designation) params.designation = filters.designation;
      if (filters.from_date) {
        params.from_date = dayjs(filters.from_date).format("YYYY-MM-DD");
      }
      if (filters.to_date) {
        params.to_date = dayjs(filters.to_date).format("YYYY-MM-DD");
      }

      const res = await axios.get(`${backUrl}/api/hr/siteofficefingerprint/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
        params,
      });

      const results = res.data.results || res.data || [];
      const count = res.data.count || results.length;

      const recordsWithKeys = results.map((record, index) => ({
        ...record,
        key: record.id || `${record.empid}-${record.date}-${index}`,
      }));

      setData(recordsWithKeys);
      setTotalItems(count);

      if (resetPage) {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching site office data:", error);
      message.error("Failed to fetch fingerprint records");
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    fetchData();
  }, [authData?.access, backUrl]);

  useEffect(() => {
    if (filters.organisation) {
      fetchDepartments(filters.organisation);
    } else {
      setDepartments([]);
    }
  }, [filters.organisation]);

  useEffect(() => {
    if (filters.department) {
      fetchDesignations(filters.department);
    } else {
      setDesignations([]);
    }
  }, [filters.department]);

  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    pageSize,
    filters.organisation,
    filters.department,
    filters.designation,
    filters.from_date,
    filters.to_date,
  ]);

  useEffect(() => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    const timer = setTimeout(() => {
      if (filters.emp_code || filters.emp_name) {
        fetchData(true);
      }
    }, 500);

    setSearchTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters.emp_code, filters.emp_name]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };

      if (key === "organisation") {
        newFilters.department = "";
        newFilters.designation = "";
      } else if (key === "department") {
        newFilters.designation = "";
      }

      return newFilters;
    });

    if (key !== "emp_code" && key !== "emp_name") {
      setCurrentPage(1);
    }
  };

  const handleExportExcel = async () => {
    try {
      // message.loading("Preparing export...", 0);

      if (selectedRowKeys.length > 0) {
        const rowsToExport = data.filter((row) =>
          selectedRowKeys.includes(row.key)
        );
        exportToExcel(rowsToExport);
        return;
      }

      const params = {};
      if (filters.emp_code) params.emp_code = filters.emp_code;
      if (filters.emp_name) params.employeename = filters.emp_name;
      if (filters.organisation) params.organisation = filters.organisation;
      if (filters.department) params.department = filters.department;
      if (filters.designation) params.designation = filters.designation;
      if (filters.from_date) {
        params.from_date = dayjs(filters.from_date).format("YYYY-MM-DD");
      }
      if (filters.to_date) {
        params.to_date = dayjs(filters.to_date).format("YYYY-MM-DD");
      }

      const res = await axios.get(`${backUrl}/api/hr/siteofficefingerprint/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
        params,
      });

      const allRecords = res.data.results || res.data || [];
      message.destroy();
      exportToExcel(allRecords);
    } catch (err) {
      message.destroy();
      console.error("Error exporting data:", err);
      message.error("Failed to export data");
    }
  };

  const exportToExcel = (records) => {
    if (!records.length) {
      message.warning("No records to export");
      return;
    }

    const exportData = records.map((row) => ({
      "Employee Code": row.empid || "",
      "Employee Name": row.employeename || "",
      Date: row.date || "",
      "Punch-In Time": row.time || "",
      Company: row.employee_details?.organisation || "",
      Department: row.employee_details?.department || "",
      Designation: row.employee_details?.designation || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fingerprint Records");

    XLSX.writeFile(workbook, "site_office_fingerprint.xlsx");
    message.success(`Exported ${records.length} records successfully`);
  };

  const columns = ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).map(
    (c) => c.column
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSelectedRowKeys([]);
  };

  return (
    <section style={{ padding: "10px" }}>
      <center>
        {isMobile ? (
          <p
            style={{
              fontSize: "15px",
              textTransform: "uppercase",
              color: "#0e497b",
            }}
          >
            <IdcardOutlined /> Site Office Fingerprint Records
          </p>
        ) : (
          <h5>
            <IdcardOutlined /> Site Office Fingerprint Records
          </h5>
        )}
      </center>

      <Card
        style={{
          margin: "20px",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Employee Code</label>
            <Input
              placeholder="Enter Employee Code"
              value={filters.emp_code}
              onChange={(e) => handleFilterChange("emp_code", e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Employee Name</label>
            <Input
              placeholder="Enter Employee Name"
              value={filters.emp_name}
              onChange={(e) => handleFilterChange("emp_name", e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Company</label>
            <Select
              placeholder="Select Company"
              allowClear
              style={{ width: "100%" }}
              value={filters.organisation || undefined}
              onChange={(value) =>
                handleFilterChange("organisation", value ?? "")
              }
            >
              {organisations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Department</label>
            <Select
              showSearch
              placeholder="Select Department"
              allowClear
              style={{ width: "100%" }}
              value={filters.department || undefined}
              onChange={(value) => handleFilterChange("department", value ?? "")}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              disabled={!filters.organisation}
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Designation</label>
            <Select
              showSearch
              placeholder="Select Designation"
              allowClear
              style={{ width: "100%" }}
              value={filters.designation || undefined}
              onChange={(value) =>
                handleFilterChange("designation", value ?? "")
              }
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              disabled={!filters.department}
            >
              {designations.map((des) => (
                <Option key={des.id} value={des.id}>
                  {des.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={10} lg={8}>
            <label>Date Range</label>
            <RangePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              value={
                filters.from_date && filters.to_date
                  ? [dayjs(filters.from_date), dayjs(filters.to_date)]
                  : null
              }
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  setFilters({
                    ...filters,
                    from_date: dates[0],
                    to_date: dates[1],
                  });
                  setCurrentPage(1);
                } else {
                  setFilters({
                    ...filters,
                    from_date: null,
                    to_date: null,
                  });
                  setCurrentPage(1);
                }
              }}
            />
          </Col>
        </Row>
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "15px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Button
            icon={<DownloadOutlined />}
            style={{
              height: "36px",
              width: "220px",
              backgroundColor: "#0e497b",
              color: "white",
            }}
            onClick={handleExportExcel}
            disabled={loading}
          >
            Download{" "}
            {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "All"}
          </Button>

          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            open={columnDropdownOpen}
            onOpenChange={setColumnDropdownOpen}
            dropdownRender={() => (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  padding: 12,
                  width: 280,
                  boxShadow: "0 3px 8px rgba(0,0,0,.15)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: 8,
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </Button>

                  <Button
                    type="link"
                    size="small"
                    onClick={handleDeselectAll}
                  >
                    Deselect
                  </Button>

                  <Button
                    type="link"
                    size="small"
                    onClick={handleResetDefault}
                  >
                    Reset
                  </Button>
                </div>

                <div
                  style={{
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  {ALL_COLUMNS.map((col) => (
                    <div key={col.key} style={{ marginBottom: 6 }}>
                      <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => handleColumnToggle(col.key)}
                      >
                        {col.title}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </div>
            )}
          >
            <Tooltip title="Customize Columns">
              <Button icon={<ColumnHeightOutlined />}>
                Custom Columns ({visibleColumns.length}/{ALL_COLUMNS.length})
              </Button>
            </Tooltip>
          </Dropdown>
        </div>

        {selectedRowKeys.length > 0 && (
          <Button onClick={() => setSelectedRowKeys([])}>
            Clear Selection
          </Button>
        )}

      </div>

      <Spin spinning={loading} tip="Loading fingerprint records...">
        <Table
          style={{ marginTop: "10px" }}
          columns={columns}
          dataSource={data}
          bordered
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowSelection={rowSelection}
          rowKey="key"
          components={{
            header: {
              cell: (props) => (
                <th
                  {...props}
                  style={{
                    backgroundColor: "#0e497b",
                    color: "white",
                  }}
                >
                  {props.children}
                </th>
              ),
            },
          }}
        />
      </Spin>
    </section>
  );
}